/**
 * Created: 2026-09-21
 * Refactor: Convert to DataGrid layout with consistent toolbar
 *
 * Staff management page - list, add, edit roles, and remove site staff members.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useCallback, useState } from 'react'
import ActionQrDialog from '../../components/ActionQrDialog'
import UserAvatar from '../../components/UserAvatar'
import {
  AdminUsersDocument,
  type AdminUsersQuery,
  DeleteSiteUserDocument,
  GetCurrentUserDocument,
  SetSiteUserRolesDocument,
  SiteRole,
} from '../../gql/graphql'
import { useRole } from '../../hooks'

gql`
  query AdminUsers($siteId: UUID!) {
    adminUsers(siteId: $siteId) {
      id
      openid
      name
      avatar
      mobile
      gender
      roles
    }
  }
`

gql`
  mutation SetSiteUserRoles($siteId: ID!, $userId: ID!, $roles: [SiteRole!]!) {
    setSiteUserRoles(siteId: $siteId, userId: $userId, roles: $roles) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

gql`
  mutation DeleteSiteUser($siteId: ID!, $userId: ID!) {
    deleteSiteUser(siteId: $siteId, userId: $userId) {
      ... on GraphqlSuccess {
        success
      }
    }
  }
`

type StaffUser = NonNullable<AdminUsersQuery['adminUsers']>[number]

function buildColumns(
  currentUserId: string | null,
  onEditRoles: (user: StaffUser) => void,
  onDelete: (user: StaffUser) => void,
): GridColDef<StaffUser>[] {
  return [
    {
      field: 'user',
      headerName: '用户',
      flex: 1,
      minWidth: 200,
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <UserAvatar user={params.row} />
        </Box>
      ),
    },
    {
      field: 'roles',
      headerName: '角色',
      width: 300,
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.value
            .map((role: SiteRole) => {
              if (role === SiteRole.SuperAdmin) return '超级管理员'
              if (role === SiteRole.Admin) return '管理员'
              if (role === SiteRole.Coach) return '教练'
              return '成员'
            })
            .join(', ')}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          <Tooltip title="编辑角色">
            <IconButton size="small" onClick={() => onEditRoles(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.id !== currentUserId && (
            <Tooltip title="删除">
              <IconButton size="small" onClick={() => onDelete(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]
}

export default function StaffPage() {
  const { currentSite } = useRole()
  const { data, loading, error, refetch } = useQuery(AdminUsersDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })
  const { data: currentUserData } = useQuery(GetCurrentUserDocument)
  const [deleteSiteUser] = useMutation(DeleteSiteUserDocument)
  const [setSiteUserRoles] = useMutation(SetSiteUserRolesDocument)
  const users = data?.adminUsers ?? []

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<StaffUser | null>(null)
  const [roleSelectionOpen, setRoleSelectionOpen] = useState(false)
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [selectedAdminRole, setSelectedAdminRole] = useState<SiteRole | ''>('')
  const [selectedIsCoach, setSelectedIsCoach] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [editingUserWasSuperAdmin, setEditingUserWasSuperAdmin] = useState(false)

  const currentUserId =
    (currentUserData?.currentUser as { id?: string | null })?.id ?? null

  const handleDeleteClick = (user: StaffUser) => {
    setUserToDelete(user)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete || !currentSite) return

    try {
      await deleteSiteUser({
        variables: {
          siteId: currentSite.id,
          userId: userToDelete.id as string,
        },
      })
      await refetch()
    } catch (err) {
      message.error(`删除失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setDeleteDialogOpen(false)
      setUserToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setUserToDelete(null)
  }

  const selectedRoles = [
    ...(selectedAdminRole ? [selectedAdminRole] : []),
    ...(selectedIsCoach ? [SiteRole.Coach] : []),
  ]

  const handleQrScanned = useCallback(
    async (user: { id?: string | null; openid: string }) => {
      if (!currentSite || !user.id) {
        message.error('未找到用户')
        setAddUserDialogOpen(false)
        return
      }

      const existingUser = users.find((u) => u.id === user.id)
      if (existingUser && existingUser.roles.length > 0) {
        message.error('该用户已经是站点成员，无法重复添加')
        setAddUserDialogOpen(false)
        return
      }

      try {
        const result = await setSiteUserRoles({
          variables: {
            siteId: currentSite.id,
            userId: user.id,
            roles: [
              ...(selectedAdminRole ? [selectedAdminRole] : []),
              ...(selectedIsCoach ? [SiteRole.Coach] : []),
            ],
          },
        })

        if (result.data?.setSiteUserRoles.__typename === 'GraphqlRequestError') {
          message.error(`添加失败: ${result.data.setSiteUserRoles.message}`)
          setAddUserDialogOpen(false)
          return
        }

        await refetch()
        setAddUserDialogOpen(false)
      } catch (err) {
        message.error(`添加失败: ${err instanceof Error ? err.message : err}`)
        setAddUserDialogOpen(false)
      }
    },
    [currentSite, setSiteUserRoles, selectedAdminRole, selectedIsCoach, refetch, users],
  )

  const handleAddUserClick = () => {
    setEditingUserId(null)
    setSelectedAdminRole('')
    setSelectedIsCoach(false)
    setEditingUserWasSuperAdmin(false)
    setRoleSelectionOpen(true)
  }

  const handleEditRolesClick = (user: StaffUser) => {
    setEditingUserId(user.id as string)
    if (user.roles.includes(SiteRole.SuperAdmin)) {
      setSelectedAdminRole(SiteRole.SuperAdmin)
    } else if (user.roles.includes(SiteRole.Admin)) {
      setSelectedAdminRole(SiteRole.Admin)
    } else {
      setSelectedAdminRole('')
    }
    setSelectedIsCoach(user.roles.includes(SiteRole.Coach))
    setEditingUserWasSuperAdmin(
      user.id === currentUserId && user.roles.includes(SiteRole.SuperAdmin),
    )
    setRoleSelectionOpen(true)
  }

  const handleRoleConfirm = async () => {
    if (!editingUserId) {
      if (selectedRoles.length === 0) {
        setRoleSelectionOpen(false)
        return
      }
      setRoleSelectionOpen(false)
      setAddUserDialogOpen(true)
      return
    }

    if (!currentSite) return

    if (selectedRoles.length === 0) {
      setDeleteConfirmOpen(true)
      return
    }

    try {
      const result = await setSiteUserRoles({
        variables: {
          siteId: currentSite.id,
          userId: editingUserId,
          roles: selectedRoles,
        },
      })

      if (result.data?.setSiteUserRoles.__typename === 'GraphqlRequestError') {
        message.error(`修改失败: ${result.data.setSiteUserRoles.message}`)
        return
      }

      message.success('角色已修改')
      await refetch()
      setRoleSelectionOpen(false)
      setEditingUserId(null)
    } catch (err) {
      message.error(`修改失败: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleRoleCancel = () => {
    setRoleSelectionOpen(false)
    setEditingUserId(null)
  }

  const handleDeleteConfirmYes = async () => {
    if (!editingUserId || !currentSite) return

    try {
      await deleteSiteUser({
        variables: {
          siteId: currentSite.id,
          userId: editingUserId,
        },
      })
      await refetch()
    } catch (err) {
      message.error(`删除失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setDeleteConfirmOpen(false)
      setRoleSelectionOpen(false)
      setEditingUserId(null)
    }
  }

  const handleDeleteConfirmNo = () => {
    setDeleteConfirmOpen(false)
  }

  const columns = buildColumns(currentUserId, handleEditRolesClick, handleDeleteClick)

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        人员设置
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          刷新
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddUserClick}
        >
          添加用户
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <DataGrid
        rows={users}
        columns={columns}
        loading={loading}
        autoHeight
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
      />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} disableRestoreFocus>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            确定要删除用户 "{userToDelete?.name}" 吗？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>取消</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleSelectionOpen} onClose={handleRoleCancel} disableRestoreFocus>
        <DialogTitle>{editingUserId ? '修改用户角色' : '选择用户角色'}</DialogTitle>
        <DialogContent>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAdminRole === SiteRole.Admin}
                    onChange={(e) =>
                      setSelectedAdminRole(e.target.checked ? SiteRole.Admin : '')
                    }
                    disabled={editingUserWasSuperAdmin}
                  />
                }
                label="管理员"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAdminRole === SiteRole.SuperAdmin}
                    onChange={(e) =>
                      setSelectedAdminRole(e.target.checked ? SiteRole.SuperAdmin : '')
                    }
                    disabled={editingUserWasSuperAdmin}
                  />
                }
                label="超级管理员"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedIsCoach}
                    onChange={(e) => setSelectedIsCoach(e.target.checked)}
                  />
                }
                label="教练"
              />
            </FormGroup>
            {editingUserWasSuperAdmin && (
              <Typography variant="caption" color="text.secondary">
                不能移除自己的超级管理员角色，请联系其他超级管理员操作。
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRoleCancel}>取消</Button>
          <Button onClick={handleRoleConfirm} variant="contained">
            确定
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteConfirmNo}
        disableRestoreFocus
      >
        <DialogTitle>确认删除用户</DialogTitle>
        <DialogContent>
          <DialogContentText>
            未选择任何角色，是否将该用户从人员列表中删除？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmNo}>取消</Button>
          <Button onClick={handleDeleteConfirmYes} color="error" variant="contained">
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {currentSite && (
        <ActionQrDialog
          open={addUserDialogOpen}
          onClose={() => setAddUserDialogOpen(false)}
          action="addstaff"
          actionId={currentSite.id}
          title="扫码添加用户"
          onScanned={handleQrScanned}
        />
      )}
    </Box>
  )
}
